import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import NestedList from "../pages/biobank/dashboard/NestedList";
import logo from "assets/BiosciencesAndTechnology.svg";
import "styles/Sidebar.css";
import { useAuth } from "./AuthProvider";
import { useNavigation } from "../hooks/useNavigation";
import { getNavigationItems } from "../constants/navigation";
import { getUserPermissionsByUserId } from "../utils/api";

const Navigation = ({ userProfile }) => {
  const { groups, user } = useAuth();
  const navigationState = useNavigation();
  const location = useLocation();
  const [userPermissions, setUserPermissions] = useState([]);
  const [filteredNavItems, setFilteredNavItems] = useState([]);
  const [filteredBottomNavItems, setFilteredBottomNavItems] = useState([]);

  // Load user permissions from API
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (user?.id) {
        try {
          const permissions = await getUserPermissionsByUserId(user.id);
          setUserPermissions(permissions);
        } catch (error) {
          console.error("Error loading user permissions:", error);
        }
      }
    };

    loadUserPermissions();
  }, [user?.id]);

  // Get navigation items based on user groups and current location
  useEffect(() => {
    const {
      main: navItems,
      bottom: bottomNavItems,
      topLevel,
    } = getNavigationItems(groups, location.pathname);

    // Filter navigation items based on user permissions
    const filterNavigation = (items) => {
      if (!userPermissions || userPermissions.length === 0) {
        // No permissions loaded yet, show all items
        return items;
      }

      return items
        .map((item) => {
          const filteredItem = { ...item };

          // Check if user has permission for this module
          const hasModulePermission = userPermissions.some(
            (perm) => perm.module?.toLowerCase() === item.text?.toLowerCase()
          );

          // Check if this is a top-level item (Admin, BioBank, Researcher)
          if (
            item.group === "admin" ||
            item.group === "biobank" ||
            item.group === "researcher"
          ) {
            // For top-level items, check permissions for the group
            const groupPermissions = userPermissions.filter(
              (perm) =>
                perm.module &&
                ["Admin", "BioBank", "Researcher"].some((g) =>
                  perm.module.toLowerCase().includes(g.toLowerCase())
                )
            );

            // If no group-specific permissions exist, show all items
            if (groupPermissions.length === 0) {
              return filteredItem;
            }

            // Check if user has access to this group
            const hasGroupAccess = groupPermissions.some(
              (perm) =>
                (item.group === "admin" &&
                  perm.module.toLowerCase().includes("admin")) ||
                (item.group === "biobank" &&
                  perm.module.toLowerCase().includes("biobank")) ||
                (item.group === "researcher" &&
                  perm.module.toLowerCase().includes("researcher"))
            );

            if (!hasGroupAccess) {
              return null;
            }

            // Filter children based on permissions
            if (item.children) {
              filteredItem.children = item.children.filter((child) => {
                const childPermission = userPermissions.find(
                  (perm) =>
                    perm.module &&
                    (perm.module.toLowerCase() === child.text.toLowerCase() ||
                      perm.module
                        .toLowerCase()
                        .replace(/\s+/g, "")
                        .includes(child.text.toLowerCase().replace(/\s+/g, "")))
                );

                // If permission exists, check if it's enabled
                if (childPermission) {
                  return childPermission.permission !== "None";
                }

                // If no specific permission found, allow by default
                return true;
              });
            }

            return filteredItem;
          }

          // For child items, check specific permissions
          if (item.children) {
            filteredItem.children = item.children.filter((child) => {
              return true; // Allow children by default unless filtered above
            });
          }

          return filteredItem;
        })
        .filter((item) => item !== null);
    };

    const filtered = filterNavigation(navItems);
    setFilteredNavItems(filtered);
    setFilteredBottomNavItems(bottomNavItems);

    // Log differences
    const hiddenItems = navItems.filter(
      (item) => !filtered.some((fItem) => fItem.text === item.text)
    );

    if (hiddenItems.length > 0) {
      console.log("=== NAVIGATION PERMISSION FILTERING ===");
      console.log("User ID:", user?.id);
      console.log("User Permissions:", userPermissions);
      console.log(
        "Hidden Navigation Items:",
        hiddenItems.map((item) => item.text)
      );
      console.log(
        "Visible Navigation Items:",
        filtered.map((item) => item.text)
      );

      // Log permission details
      filtered.forEach((item) => {
        const itemPermissions = userPermissions.filter((perm) =>
          perm.module?.toLowerCase().includes(item.text.toLowerCase())
        );
        if (itemPermissions.length > 0) {
          console.log(`Permissions for "${item.text}":`, itemPermissions);
        }
      });
      console.log("=== END NAVIGATION PERMISSION FILTERING ===");
    }
  }, [groups, userPermissions, user?.id, location.pathname]);

  return (
    <aside className="sidebar">
      <div style={{ margin: "25px 15px 25px 15px" }}>
        <img
          src={logo}
          alt="Logo"
          className="logo-image"
          style={{
            margin: "0",
            width: "174px",
            height: "auto",
            display: "block",
          }}
        />
      </div>
      <nav
        className="sidebar-nav"
        role="navigation"
        aria-label="Main navigation"
      >
        <NestedList
          items={filteredNavItems}
          navigationState={navigationState}
        />
      </nav>
      <nav
        className="sidebar-bottom"
        role="navigation"
        aria-label="User actions"
      >
        <NestedList
          items={filteredBottomNavItems}
          navigationState={navigationState}
        />
      </nav>
    </aside>
  );
};

export default Navigation;
